<template>
  <div :class="{ 'issue-field': true, 'issue-field--editing': editing, 'issue-field--error': hasError }">
    <div class="issue-field__wrapper">
      <div
        v-if="canEdit && !editing"
        class="issue-field__menu"
      >
        <div class="dropdown">
          <Popper
            v-model:open="open"
            placement="bottom-end"
          >
            <template #trigger>
              <div class="dropdown__trigger">
                <UseSvg
                  class="icon"
                  name="vertical_ellipsis"
                />
              </div>
            </template>

            <div class="menu menu--dropdown">
              <ul class="menu__items">
                <li class="menu-item menu-item--dropdown typo-list-item">
                  <button
                    class="menu-item__link"
                    type="button"
                    @click="beginEdit"
                  >
                    <span class="menu-item__label">
                      {{ $t('general.edit_field', { field_name: label.toLowerCase() }) }}
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </Popper>
        </div>
      </div>

      <!-- Display the field title -->
      <div
        v-if="label && showLabel"
        class="issue-field__header"
      >
        <label
          class="issue-field__title"
          :for="field"
        >{{ label }}</label>
      </div>

      <div class="issue-field__body typo-field-text">
        <textarea
          :id="field"
          ref="input"
          v-model="internalValue"
          v-autoresize
          class="issue__input typo-ignore"
          :disabled="!editing"
          :placeholder="$t(`issues.${field}_placeholder`)"
          rows="1"
          @input="onInput"
        />
      </div>
    </div>

    <!-- Actions -->
    <div
      v-if="canEdit && editing && issue.id"
      class="issue-field__actions"
    >
      <div
        v-if="errors"
        class="issue-field__errors"
      >
        <FormRow
          :errors="errors"
          :modifiers="['narrow']"
        />
      </div>

      <button
        class="button"
        type="button"
        @click="cancelEdit"
      >
        {{ $t('general.cancel') }}
      </button>
      <button
        class="button button--inverse"
        type="button"
        @click="updateField"
      >
        {{ $t('general.update_field', { field_name: label.toLowerCase() }) }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import useAjax from '@/functions/use_ajax';

import vAutoresize from '@/directives/autoresize';

import FormRow from '@/components/form/row.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';

const props = defineProps({
  issue: { type: Object, required: true },
  field: { type: String, required: true },
  placeholder: { type: String, default: '' },
  showLabel: { type: Boolean, default: true },
  error: { type: Boolean, default: false },
  canEdit: Boolean,
});
const emit = defineEmits(['update']);

const route = useRoute();
const ajax = useAjax();

const open = ref(false);
const editing = ref(!props.issue.id);
const input = ref(null);
const internalValue = ref(props.issue[props.field]);
const errors = ref(null);
const isShaftV2 = computed(() => route.path.includes('shafts_v2'));

const operationData = computed(() => {
  if (route.params.shaftId) {
    if (isShaftV2.value) return {
      name: 'shafts_v2',
      url: `shafts_v2/shafts/${route.params.shaftId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}`,
      body: { issue: { [props.field]: internalValue.value } }
    };
    return {
      name:'shafts',
      url: `shafts/${route.params.shaftId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}`,
      body: { activity: { detail_attributes: { [props.field]: internalValue.value } } }
    };
  } else if (route.params.shipId) {
    return {
      name:'ships',
      url: `ships/${route.params.shipId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}`,
      body: { issue: { [props.field]: internalValue.value } }
    };
  } else if (route.params.equipmentId) {
    return {
      name:'equipment',
      url: `equipment/${route.params.equipmentId}/inspections/${props.issue.inspection_id}/issues/${props.issue.id}`,
      body: { issue: { [props.field]: internalValue.value } }
    };
  } else {
    return {
      name:'plants',
      url:`operations/${route.params.operationId}/plants/inspections/${props.issue.inspection_id}/issues/${props.issue.id}`,
      body: { issue: { [props.field]: internalValue.value } }
    };
  }
});

const isShaft = computed(() => {
  return operationData.value.name === 'shafts';
});

const label = computed(() => {
  return I18n.t(`issues.${props.field}`);
});

const hasError = computed(() => {
  return !!(props.error || errors.value);
});

async function beginEdit() {
  editing.value = true;
  open.value = false;

  await nextTick();
  input.value.focus();
  input.value.select();
}

async function updateField() {
  errors.value = null;

  let { organisationId } = route.params;
  let path = `/organisations/${organisationId}/${operationData.value.url}`;

  let { ok, data } = await ajax(path, {
    method: 'PATCH',
    body: operationData.value.body,
  });

  if (ok) {
    props.issue[props.field] = internalValue.value;
    editing.value = false;
    emit('update', data);
  } else {
    if (isShaft.value) {
      errors.value = data.activity[`detail.${props.field}`];
    } else {
      errors.value = data.issue[props.field];
    }
  }
}

function cancelEdit() {
  internalValue.value = props.issue[props.field];
  editing.value = false;
  errors.value = null;
}

function onInput({ target: { value } }) {
  if (props.issue.id) return;

  props.issue[props.field] = value;
}

defineExpose({internalValue});
</script>
